<template>
  <div class="visual-building-showcase">
    <!-- Hero Section -->
    <section class="visual-hero-section">
      <div class="animated-bg">
        <div class="floating-orb orb-1"></div>
        <div class="floating-orb orb-2"></div>
        <div class="floating-orb orb-3"></div>
        <div class="floating-orb orb-4"></div>
        <div class="gradient-mesh"></div>
      </div>
      <div class="visual-hero-content">
        <div class="hero-badge">
          <v-icon class="badge-icon">mdi-sparkles</v-icon>
          <span class="badge-text">{{ t('visualization.hero.badge') }}</span>
        </div>
        <h1 class="visual-hero-title">
          <span class="title-line">{{ t('visualization.hero.title.line1') }}</span>
          <span class="title-line gradient-text">{{ t('visualization.hero.title.line2') }}</span>
        </h1>
        <p class="visual-hero-subtitle">
          {{ t('visualization.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- Section 1: 2D Map View -->
    <section class="visual-showcase-section">
      <div class="visual-section-container">
        <div class="visual-section-header">
          <div class="section-badge">{{ t('visualization.sections.map2d.badge') }}</div>
          <div class="section-title-group">
            <h2 class="visual-section-title">{{ t('visualization.sections.map2d.title') }}</h2>
            <p class="section-subtitle">{{ t('visualization.sections.map2d.subtitle') }}</p>
          </div>
        </div>
        
        <div class="visual-component-wrapper">
          <FloorPlan2D />
        </div>

        <div class="visual-section-description">
          <v-icon class="description-icon">mdi-map-marker</v-icon>
          <p>{{ t('visualization.sections.map2d.description') }}</p>
        </div>
      </div>
    </section>

    <!-- Section 2: 2D Floor Plan -->
    <section class="visual-showcase-section visual-alt-bg">
      <div class="visual-section-container">
        <div class="visual-section-header">
          <div class="section-badge">{{ t('visualization.sections.floor2d.badge') }}</div>
          <div class="section-title-group">
            <h2 class="visual-section-title">{{ t('visualization.sections.floor2d.title') }}</h2>
            <p class="section-subtitle">{{ t('visualization.sections.floor2d.subtitle') }}</p>
          </div>
        </div>
        
        <div class="visual-component-wrapper">
          <FloorPlan3D />
        </div>

        <div class="visual-section-description">
          <v-icon class="description-icon">mdi-home-outline</v-icon>
          <p>{{ t('visualization.sections.floor2d.description') }}</p>
        </div>
      </div>
    </section>

    <!-- Section 3: 3D Floor Model -->
    <section class="visual-showcase-section">
      <div class="visual-section-container">
        <div class="visual-section-header">
          <div class="section-badge">{{ t('visualization.sections.floor3d.badge') }}</div>
          <div class="section-title-group">
            <h2 class="visual-section-title">{{ t('visualization.sections.floor3d.title') }}</h2>
            <p class="section-subtitle">{{ t('visualization.sections.floor3d.subtitle') }}</p>
          </div>
        </div>
        
        <div class="visual-component-wrapper">
          <FloorModel3D />
        </div>

        <div class="visual-section-description">
          <v-icon class="description-icon">mdi-palette-outline</v-icon>
          <p>{{ t('visualization.sections.floor3d.description') }}</p>
        </div>
      </div>
    </section>

    <!-- Section 4: Building Overview -->
    <section class="visual-showcase-section visual-alt-bg">
      <div class="visual-section-container">
        <div class="visual-section-header">
          <div class="section-badge">{{ t('visualization.sections.building.badge') }}</div>
          <div class="section-title-group">
            <h2 class="visual-section-title">{{ t('visualization.sections.building.title') }}</h2>
            <p class="section-subtitle">{{ t('visualization.sections.building.subtitle') }}</p>
          </div>
        </div>
        
        <div class="visual-component-wrapper">
          <OutsideBuildingView />
        </div>

        <div class="visual-section-description">
          <v-icon class="description-icon">mdi-domain</v-icon>
          <p>{{ t('visualization.sections.building.description') }}</p>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="visual-showcase-footer">
      <div class="visual-footer-content">
        <p class="footer-title">{{ t('visualization.footer.title') }}</p>
        <p class="visual-footer-tagline">{{ t('visualization.footer.tagline') }}</p>
      </div>
    </footer>

    <!-- Scroll Progress -->
    <div class="visual-scroll-progress">
      <div class="visual-progress-bar" :style="{ width: scrollProgress + '%' }"></div>
    </div>

    <!-- Scroll to Top -->
    <transition name="visual-fade">
      <button v-if="showScrollTop" @click="scrollToTop" class="visual-scroll-top-btn">
        <v-icon>mdi-arrow-up</v-icon>
      </button>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import FloorPlan2D from '@/components/FloorPlan2D.vue';
import FloorPlan3D from '@/components/FloorPlan3D.vue';
import FloorModel3D from '@/components/FloorModel3D.vue';
import OutsideBuildingView from '@/components/OutsideBuildingView.vue';

const { t } = useI18n();

const showScrollTop = ref(false);
const scrollProgress = ref(0);

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  
  showScrollTop.value = scrollTop > 500;
  scrollProgress.value = (scrollTop / scrollHeight) * 100;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.visual-building-showcase {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  color: #1e293b;
  position: relative;
  overflow-x: hidden;
}

/* ==================== Animated Background ==================== */
.animated-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: floatGentle 20s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(147, 51, 234, 0.12) 0%, transparent 70%);
  top: 30%;
  right: -5%;
  animation-delay: 2s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%);
  bottom: 10%;
  left: 20%;
  animation-delay: 4s;
}

.orb-4 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%);
  bottom: 40%;
  right: 15%;
  animation-delay: 6s;
}

.gradient-mesh {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(at 20% 30%, rgba(59, 130, 246, 0.08) 0px, transparent 50%),
    radial-gradient(at 80% 70%, rgba(147, 51, 234, 0.06) 0px, transparent 50%),
    radial-gradient(at 50% 50%, rgba(14, 165, 233, 0.05) 0px, transparent 50%);
  animation: meshRotate 30s linear infinite;
}

@keyframes floatGentle {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(20px, 30px) scale(1.02); }
}

@keyframes meshRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ==================== Hero Section ==================== */
.visual-hero-section {
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 2rem 80px;
  position: relative;
  text-align: center;
}

.visual-hero-content {
  position: relative;
  z-index: 1;
  max-width: 1000px;
  animation: fadeInUp 1s ease-out;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 50px;
  margin-bottom: 32px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.08);
  backdrop-filter: blur(10px);
}

.badge-icon {
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.1); }
}

.badge-text {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #3b82f6;
}

.visual-hero-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 28px 0;
  letter-spacing: -2px;
}

.title-line {
  display: block;
}

.gradient-text {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1e40af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.visual-hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  line-height: 1.8;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
  font-weight: 400;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== Sections ==================== */
.visual-showcase-section {
  padding: 6rem 2rem;
  position: relative;
}

.visual-showcase-section.visual-alt-bg {
  background: rgba(255, 255, 255, 0.4);
}

.visual-section-container {
  max-width: 1400px;
  margin: 0 auto;
}

.visual-section-header {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 3rem;
}

.section-badge {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.2);
  flex-shrink: 0;
}

.section-title-group {
  flex: 1;
}

.visual-section-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -1px;
  margin: 0 0 0.5rem 0;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 400;
}

/* ==================== Component Wrapper ==================== */
.visual-component-wrapper {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.08),
    0 8px 24px rgba(59, 130, 246, 0.06);
  border: 1px solid rgba(59, 130, 246, 0.1);
  margin-bottom: 2.5rem;
  height: 700px;
  display: flex;
  backdrop-filter: blur(10px);
}

.visual-component-wrapper > * {
  width: 100%;
  height: 100%;
}

.visual-section-description {
  position: relative;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.03) 100%);
  border-left: 4px solid #3b82f6;
  padding: 2rem 2.5rem 2rem 5rem;
  border-radius: 16px;
  margin-top: 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.description-icon {
  position: absolute;
  left: 2rem;
  top: 2rem;
  font-size: 2rem;
}

.visual-section-description p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #475569;
  text-align: justify;
  margin: 0;
}

/* ==================== Footer ==================== */
.visual-showcase-footer {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.9) 100%);
  padding: 3rem 2rem;
  text-align: center;
  border-top: 1px solid rgba(59, 130, 246, 0.1);
}

.visual-footer-content p {
  margin: 0.5rem 0;
  color: #64748b;
}

.footer-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1e293b;
}

.visual-footer-tagline {
  font-style: italic;
  font-size: 0.9rem;
}

/* ==================== Scroll Progress ==================== */
.visual-scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(59, 130, 246, 0.1);
  z-index: 999;
}

.visual-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width 0.1s ease;
}

/* ==================== Scroll to Top Button ==================== */
.visual-scroll-top-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
  z-index: 99;
}

.visual-scroll-top-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.4);
}

.visual-scroll-top-btn:active {
  transform: translateY(-2px);
}

/* ==================== Transitions ==================== */
.visual-fade-enter-active,
.visual-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.visual-fade-enter-from,
.visual-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* ==================== Responsive Design ==================== */
@media (max-width: 1024px) {
  .visual-section-header {
    gap: 1.5rem;
  }
  
  .section-badge {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }
  
  .visual-component-wrapper {
    height: 600px;
  }
  
  .visual-section-description {
    padding: 1.5rem 2rem 1.5rem 4.5rem;
  }
  
  .description-icon {
    left: 1.5rem;
    top: 1.5rem;
    font-size: 1.75rem;
  }
}

@media (max-width: 768px) {
  .visual-hero-section {
    padding: 24px 1.5rem 60px;
  }
  
  .visual-showcase-section {
    padding: 4rem 1.5rem;
  }
  
  .visual-section-header {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  .section-badge {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }
  
  .visual-component-wrapper {
    height: 500px;
    border-radius: 16px;
  }
  
  .visual-section-description {
    padding: 1.25rem 1.5rem 1.25rem 4rem;
  }
  
  .description-icon {
    left: 1.25rem;
    top: 1.25rem;
    font-size: 1.5rem;
  }
  
  .visual-section-description p {
    font-size: 1rem;
    text-align: left;
  }
  
  .visual-scroll-top-btn {
    width: 48px;
    height: 48px;
    bottom: 1.5rem;
    right: 1.5rem;
  }
}

@media (max-width: 480px) {
  .visual-hero-section {
    padding: 80px 1rem 50px;
  }
  
  .hero-badge {
    padding: 8px 18px;
  }
  
  .badge-text {
    font-size: 11px;
  }
  
  .visual-showcase-section {
    padding: 3rem 1rem;
  }
  
  .section-badge {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .visual-component-wrapper {
    height: 400px;
    border-radius: 12px;
  }
  
  .visual-section-description {
    padding: 1rem 1.25rem 1rem 3.5rem;
    border-radius: 12px;
  }
  
  .description-icon {
    left: 1rem;
    top: 1rem;
    font-size: 1.25rem;
  }
  
  .visual-section-description p {
    font-size: 0.9rem;
    line-height: 1.6;
  }
  
  .visual-showcase-footer {
    padding: 2rem 1rem;
  }
}
</style>